<script setup>
import { deleteRequest, getRequest, request } from "@/utils/request.js";
import {
  DeleteOutlined,
  EditOutlined,
  ImportOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import JobTypeKeywordFormModal from "@/views/jobType/jobTypeKeyword/modules/JobTypeKeywordFormModal.vue";
import JobTypeKeywordCategoryFormModal from "@/views/jobType/jobTypeKeyword/modules/JobTypeKeywordCategoryFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const columns = [
  {
    key: "title",
    dataIndex: "title",
    title: "偏好标题",
    width: "20%",
  },
  {
    key: "categoryName",
    dataIndex: "categoryName",
    title: "所属分类",
    width: "20%",
  },
  {
    key: "multipleChoice",
    dataIndex: "multipleChoice",
    title: "多选/单选",
    width: "10%",
  },
  {
    key: "options",
    dataIndex: "options",
    title: "选项",
    width: "20%",
    customRender: ({ text, record, index, column }) => {
      if (Array.isArray(text)) {
        return text.map((item) => item.name).join(",");
      }
    },
  },
  {
    key: "sort",
    dataIndex: "sort",
    title: "排序",
    width: "3%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/jobTypeKeyword", requestBody)
    .then((response) => {
      if (response.success) {
        datasource.value = response.data.records;
        pagination.total = response.data.total;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = (record) => {
  modalRef.value.title = "职位关键字新增";
  modalRef.value.handleAdd({
    ...record,
    categoryId: searchFormModel.value.categoryId,
  });
};

const handleEdit = (record) => {
  modalRef.value.title = "职位关键字编辑";
  modalRef.value.handleEdit(record);
};

const handleDelete = (record) => {
  deleteRequest(`/jobTypeKeyword/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const category = ref([]);
const loadCategory = () => {
  getRequest("/jobTypeKeywordCategory/tree").then((response) => {
    if (response.success) {
      category.value = response.data;
    }
  });
};

const categoryModalRef = ref();
const handleCategoryAdd = (record) => {
  categoryModalRef.value.title = "职位关键字分类新增";
  categoryModalRef.value.handleAdd(record);
};

const handleCategoryEdit = (record) => {
  categoryModalRef.value.title = "职位关键字编辑";
  categoryModalRef.value.handleEdit(record);
};

const handleCategorySelect = (selectedKeys) => {
  console.info("selectedKeys", selectedKeys);
  if (selectedKeys && selectedKeys.length) {
    searchFormModel.value.categoryId = selectedKeys[0];
  } else {
    searchFormModel.value.categoryId = null;
  }
  loadData();
};

const handleCategoryRemove = (id) => {
  deleteRequest(`/jobTypeKeywordCategory/${id}`)
    .then((response) => {
      if (response.success) {
        message.success("删除成功");
      } else {
        message.error("删除失败。" + response.message);
      }
    })
    .finally(() => {
      loadCategory();
    });
};

const leftTreeHeight = ref(0);
const calculateLeftTreeHeight = () => {
  leftTreeHeight.value = window.innerHeight - 38 - 60 - 24 - 42 - 24;
};

onMounted(() => {
  loadData();
  loadCategory();
  calculateLeftTreeHeight();
  window.addEventListener("resize", calculateLeftTreeHeight);
});
</script>

<template>
  <div>
    <a-row :gutter="[10, 10]">
      <a-col :span="6">
        <a-card>
          <a-row :gutter="[10, 10]">
            <a-col :span="24" style="display: flex; gap: 10px">
              <a-button :icon="h(PlusOutlined)" @click="handleCategoryAdd">
                添加分组
              </a-button>
              <a-button :icon="h(ReloadOutlined)" @click="loadCategory">
                刷新
              </a-button>
            </a-col>
            <a-col :span="24">
              <a-tree
                v-if="category.length"
                defaultExpandAll
                :show-line="true"
                :show-icon="false"
                blockNode
                :tree-data="category"
                :field-names="{
                  children: 'children',
                  title: 'name',
                  key: 'id',
                }"
                @select="handleCategorySelect"
              >
                <template #title="{ name, id, parentId }">
                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                    "
                  >
                    <div>
                      {{ name }}
                    </div>
                    <div>
                      <a-popconfirm
                        :title="`是否删除分组【${name}】?`"
                        @click.prevent.stop
                        @confirm="handleCategoryRemove(id)"
                      >
                        <a-button :icon="h(DeleteOutlined)" type="link">
                        </a-button>
                      </a-popconfirm>
                      <a-button
                        :icon="h(PlusOutlined)"
                        type="link"
                        @click.prevent.stop="
                          handleCategoryAdd({ parentId: id })
                        "
                      >
                      </a-button>
                      <a-button
                        :icon="h(EditOutlined)"
                        type="link"
                        @click.prevent.stop="
                          handleCategoryEdit({ name, id, parentId })
                        "
                      >
                      </a-button>
                    </div>
                  </div>
                </template>
              </a-tree>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
      <a-col :span="18">
        <a-card>
          <a-form
            ref="formRef"
            layout="inline"
            :labelCol="{ span: 3, offset: 1 }"
            :model="searchFormModel"
            @keydown.enter="loadData"
          >
            <a-row :gutter="[10, 10]" style="width: 100%">
              <a-col :span="6">
                <a-form-item label="标题" name="title">
                  <a-input
                    v-model:value="searchFormModel.title"
                    placeholder="请输入标题"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="24">
                <a-button
                  type="primary"
                  style="margin-left: 18px"
                  :icon="h(SearchOutlined)"
                  @click="loadData"
                  @keydown.enter="loadData"
                >
                  查询
                </a-button>
                <a-button
                  style="margin-left: 18px"
                  :icon="h(ReloadOutlined)"
                  @click="resetForm"
                  >重置
                </a-button>
              </a-col>
            </a-row>
          </a-form>
        </a-card>
        <a-card style="margin-top: 10px">
          <div style="margin-bottom: 12px; display: flex; gap: 18px">
            <a-button
              type="primary"
              @click="handleAdd({})"
              :icon="h(PlusOutlined)"
              >新增
            </a-button>
            <!--            <a-upload-->
            <!--              :disabled="uploading"-->
            <!--              :show-upload-list="false"-->
            <!--              ref="upload"-->
            <!--              :customRequest="fileUpload"-->
            <!--              accept=".xlsx, .xls, .csv"-->
            <!--            >-->
            <!--              <a-button-->
            <!--                type="primary"-->
            <!--                :icon="h(ImportOutlined)"-->
            <!--                :loading="uploading"-->
            <!--                >导入-->
            <!--              </a-button>-->
            <!--            </a-upload>-->
          </div>
          <a-table
            row-key="id"
            :loading="loading"
            :columns="columns"
            :data-source="datasource"
            :bordered="true"
            :pagination="pagination"
            @change="tableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.dataIndex === 'multipleChoice'">
                <template v-if="record[column.dataIndex]">
                  <a-tag color="orange">多选</a-tag>
                </template>
                <template v-else>
                  <a-tag color="blue">单选</a-tag>
                </template>
              </template>
              <template v-if="column.dataIndex === 'action'">
                <a type="link" @click="handleEdit(record)">编辑</a>
                <a-divider type="vertical" />
                <a-popconfirm
                  title="确认要删除吗?"
                  @confirm="handleDelete(record)"
                >
                  <a type="link">删除</a>
                </a-popconfirm>
                <template
                  v-if="
                    ['BIG_CATEGORY', 'SMALL_CATEGORY'].includes(record['type'])
                  "
                >
                  <a-divider type="vertical" />
                  <a
                    type="link"
                    @click="
                      handleAdd({
                        parentId: record.id,
                        parentType: record.type,
                      })
                    "
                    >增加下级菜单</a
                  >
                </template>
              </template>
            </template>
          </a-table>
        </a-card>
      </a-col>
    </a-row>
    <job-type-keyword-form-modal ref="modalRef" @ok="loadData" />
    <job-type-keyword-category-form-modal
      ref="categoryModalRef"
      @ok="loadCategory"
    />
  </div>
</template>

<style scoped lang="scss"></style>
